import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";
import type { PlayableKey } from "../domain/training/missions";
import { useTrainingApi } from "./api/useTrainingApi";

const ROOT = "gavion-training";

/** The signed-in rep's training status (game progress + field missions computed by the server). */
export function useMyTraining() {
  const api = useTrainingApi();
  const { identity } = useGetIdentity();
  return useQuery({
    queryKey: [ROOT, "me", identity?.id ?? null],
    queryFn: () => api!.myStatus(),
    enabled: api !== null,
    retry: false,
    meta: { persist: false },
  });
}

/** Every active user's status (admins only). */
export function useTeamTraining(enabled: boolean) {
  const api = useTrainingApi();
  return useQuery({
    queryKey: [ROOT, "team"],
    queryFn: () => api!.teamStatus(),
    enabled: enabled && api !== null,
    retry: false,
    meta: { persist: false },
  });
}

/** Records one attempt, then refreshes the statuses (header XP, map, team tab, dashboard card). */
export function useRecordAttempt() {
  const api = useTrainingApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, score }: { key: PlayableKey; score: number }) =>
      api!.recordAttempt(key, score),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [ROOT] }),
  });
}
